import React, { useEffect, useRef, useState } from 'react';
import { FiCamera, FiTrash2 } from 'react-icons/fi';
import { format } from 'date-fns';
import { getInitials } from '../../../utils/avatar';
import { uploadAvatar, removeAvatarFile } from '../../../services/avatarService';

const ROLE_LABELS = { manager: 'Manager', admin: 'Admin', user: 'User' };

export default function ProfileCard({ user, userProfile, usage, updateProfile, alert }) {
  const [fullName, setFullName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (userProfile) setFullName(userProfile.full_name || '');
  }, [userProfile]);

  const handleSaveProfile = async () => {
    if (!user?.id) return;
    setIsSaving(true);
    setSaveMessage('');
    const { error } = await updateProfile({ full_name: fullName });
    setIsSaving(false);
    setSaveMessage(error ? 'Error saving changes' : 'Changes saved successfully!');
    if (!error) setTimeout(() => setSaveMessage(''), 3000);
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.id) return;

    setIsUploadingAvatar(true);
    try {
      const url = await uploadAvatar(user.id, file);
      const previousUrl = userProfile?.avatar_url;
      const { error } = await updateProfile({ avatar_url: url });
      if (error) throw error;
      if (previousUrl) removeAvatarFile(user.id, previousUrl).catch(() => {});
    } catch (err) {
      alert(err?.message || 'Failed to upload photo. Please try a different image.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.id || !userProfile?.avatar_url) return;
    setIsUploadingAvatar(true);
    try {
      const previousUrl = userProfile.avatar_url;
      const { error } = await updateProfile({ avatar_url: null });
      if (error) throw error;
      removeAvatarFile(user.id, previousUrl).catch(() => {});
    } catch (err) {
      alert(err?.message || 'Failed to remove photo.');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const roleLabel = ROLE_LABELS[userProfile?.role] || 'User';
  const planLabel = usage?.is_unlimited ? 'Unlimited' : usage?.plan_tier === 'trial' ? 'Trial' : (usage?.plan_tier || 'Trial');
  const statusLabel = userProfile?.is_active === false ? 'Suspended' : 'Active';

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">My Profile</h2>
        <p className="text-sm text-gray-500">This information belongs to your account only.</p>
      </div>

      <div className="flex flex-col items-center mb-8">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-2xl font-bold shadow-lg overflow-hidden">
            {userProfile?.avatar_url ? (
              <img src={userProfile.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              getInitials(fullName, user?.email)
            )}
          </div>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingAvatar}
            className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50"
            aria-label="Change avatar"
          >
            <FiCamera className="w-4 h-4 text-gray-600" />
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleAvatarSelect} />
        </div>
        <p className="text-sm text-gray-500 mt-3">
          {isUploadingAvatar ? 'Uploading…' : 'Click the camera icon to change your photo'}
        </p>
        {userProfile?.avatar_url && !isUploadingAvatar && (
          <button
            type="button"
            onClick={handleRemoveAvatar}
            className="mt-2 text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <FiTrash2 className="w-3 h-3" /> Remove photo
          </button>
        )}
      </div>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Full Name</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all hover:border-gray-400"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Email Address</label>
          <input
            type="email"
            value={user?.email || ''}
            readOnly
            disabled
            className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Role</label>
            <span className="inline-flex px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium">{roleLabel}</span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Plan</label>
            <span className="inline-flex px-4 py-2 rounded-lg bg-purple-100 text-purple-700 text-sm font-medium">{planLabel}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Account Status</label>
            <span className={`inline-flex px-4 py-2 rounded-lg text-sm font-medium ${statusLabel === 'Active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {statusLabel}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">Member Since</label>
            <p className="text-sm text-gray-700 py-2">
              {userProfile?.created_at ? format(new Date(userProfile.created_at), 'yyyy-MM-dd') : 'N/A'}
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-600 mb-2">Last Login</label>
          <p className="text-sm text-gray-700">
            {user?.last_sign_in_at ? format(new Date(user.last_sign_in_at), 'yyyy-MM-dd HH:mm:ss') : 'N/A'}
          </p>
        </div>
      </div>

      <button
        onClick={handleSaveProfile}
        disabled={isSaving}
        className="w-full mt-8 px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-medium rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? 'Saving...' : 'Save Changes'}
      </button>

      {saveMessage && (
        <p className={`text-sm mt-3 text-center ${saveMessage.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
          {saveMessage}
        </p>
      )}
    </div>
  );
}
